<template>
  <div class="animal-hunt">
    <LevelComplete v-if="isGameComplete" />
    <div v-else>
      <h1
        class="target-word"
        @dragover.prevent
        @drop="handleDrop"
        @click="speakOutLoud(targetWord)"
      >
        {{ targetWord }}
      </h1>
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
      <div class="grid">
        <div
          v-for="(animalObj, index) in shuffledAnimals"
          :key="index"
          class="animal-draggable"
          :style="draggedAnimal === animalObj ? draggedStyle : {}"
          draggable="true"
          @dragstart="handleDrag($event, animalObj)"
          @click="speakOutLoud(animalObj.word)"
          @touchstart="handleTouchStart($event, animalObj)"
          @touchmove="handleTouchMove($event)"
          @touchend="handleTouchEnd($event)"
        >
          {{ animalObj.emoji }}
        </div>
      </div>

      <div class="progress">
        <p>{{ progressMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      animals: [
        { word: "cat", emoji: "🐱" },
        { word: "dog", emoji: "🐶" },
        { word: "bird", emoji: "🐦" },
        { word: "fish", emoji: "🐟" },
        { word: "horse", emoji: "🐴" },
        { word: "frog", emoji: "🐸" },
        { word: "tiger", emoji: "🐅" },
        { word: "lion", emoji: "🦁" },
        { word: "monkey", emoji: "🐒" },
        { word: "elephant", emoji: "🐘" },
      ],
      targetWord: "",
      shuffledAnimals: [],
      message: "",
      isGameComplete: false,
      selectedVoice: null,
      touchStartX: 0,
      touchStartY: 0,
      draggedAnimal: null,
      draggedStyle: {},
      animalCounts: {},
      maxCount: 1,
    };
  },
  computed: {
    progressMessage() {
      const totalFound = Object.values(this.animalCounts).reduce(
        (a, b) => a + b,
        0
      );
      const totalWords = this.animals.length * this.maxCount;
      return `Animals found: ${totalFound} / ${totalWords}`;
    },
  },
  mounted() {
    this.initializeAnimalCounts();
    this.setupGame();
    this.setupVoices();
  },
  methods: {
    initializeAnimalCounts() {
      this.animalCounts = this.animals.reduce((acc, animalObj) => {
        acc[animalObj.word] = 0;
        return acc;
      }, {});
    },
    setupGame() {
      const availableAnimals = this.animals.filter(
        (animalObj) => this.animalCounts[animalObj.word] < this.maxCount
      );

      if (availableAnimals.length === 0) {
        this.playSound(
          "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
        );
        this.speakOutLoud("Great Job!");
        this.isGameComplete = true;
        setTimeout(() => {
          this.$router.push("/animals"); // Adjust the route as necessary
        }, 4000);
        return;
      }

      const targetObj =
        availableAnimals[Math.floor(Math.random() * availableAnimals.length)];
      this.targetWord = targetObj.word;

      this.speakOutLoud(`Find the ${this.targetWord}!`);

      const otherAnimals = this.animals.filter(
        (animalObj) => animalObj.word !== this.targetWord
      );

      // Randomly choose animals from otherAnimals
      const randomAnimals = [];
      const poolSize = Math.min(9, otherAnimals.length);
      for (let i = 0; i < poolSize; i++) {
        const randomAnimal =
          otherAnimals[Math.floor(Math.random() * otherAnimals.length)];
        randomAnimals.push(randomAnimal);
      }

      // Add target animal to the pool
      randomAnimals.push(targetObj);

      // Remove duplicates based on the word
      const uniqueAnimals = Array.from(
        new Map(randomAnimals.map((item) => [item.word, item])).values()
      );

      // Shuffle the array
      this.shuffledAnimals = uniqueAnimals.sort(() => Math.random() - 0.5);

      this.message = "";
    },
    handleDrag(event, animalObj) {
      event.dataTransfer.setData("text/plain", animalObj.word);
    },
    handleDrop(event) {
      const droppedAnimalWord = event.dataTransfer.getData("text/plain");
      if (droppedAnimalWord === this.targetWord) {
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );
        this.speakOutLoud(this.targetWord);
        this.message = "✅";
        this.animalCounts[this.targetWord]++;
        setTimeout(() => {
          this.resetGame();
        }, 3000);
      } else {
        this.message = "❌";
        setTimeout(() => {
          this.message = "";
        }, 3000);
      }
    },
    handleTouchStart(event, animalObj) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.draggedAnimal = animalObj;
      this.draggedStyle = {
        position: "absolute",
        left: `${this.touchStartX}px`,
        top: `${this.touchStartY}px`,
        zIndex: 1000,
      };
    },
    handleTouchMove(event) {
      event.preventDefault();
      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;
      this.draggedStyle = {
        ...this.draggedStyle,
        left: `${touchX}px`,
        top: `${touchY}px`,
      };
    },
    handleTouchEnd(event) {
      const dropZone = this.$el.querySelector(".target-word");
      const rect = dropZone.getBoundingClientRect();
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      if (
        touchEndX >= rect.left &&
        touchEndX <= rect.right &&
        touchEndY >= rect.top &&
        touchEndY <= rect.bottom
      ) {
        this.handleDrop({
          dataTransfer: { getData: () => this.draggedAnimal.word },
        });
      }
      this.draggedAnimal = null;
      this.draggedStyle = {};
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    resetGame() {
      this.setupGame();
    },
  },
};
</script>

<style scoped>
.animal-hunt {
  text-align: center;
}

.progress {
  text-align: center;
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.grid {
  max-width: 500px;
  margin: auto;
  margin-bottom: 30px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;
}

.target-word {
  font-size: 5rem;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
  border: 2px dotted #2196f3;
  padding: 10px;
  max-width: 350px;
  margin-inline: auto;
}

.animal-draggable {
  cursor: grab;
  border-radius: 10px;
  padding: 10px;
  font-size: 4rem;
  background-color: #81d4fa;
  font-weight: bold;
  text-align: center;
  transition: transform 0.3s;
  box-shadow: -2px 2px 2px 2px lightgray;
}

.animal-draggable:hover {
  transform: scale(1.1);
}

.message {
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
}
</style>
